/**
 * Which widgets a vendor keeps on their dashboard, and where.
 *
 * Held per USER in localStorage, deliberately on both counts.
 *
 * Per user, not per vendor: several users can belong to one vendor (the user
 * schema indexes on it), so a vendor key would let two colleagues sharing a
 * browser overwrite each other's dashboard.
 *
 * In localStorage, not on the server: an arrangement is a preference about
 * this screen on this machine. The cost is that a second laptop starts from
 * the default, which is a small price against syncing something nobody asked
 * to have synced. Losing it falls back to the default layout — a fine outcome
 * rather than a broken one.
 */
export type WidgetId = 'visits' | 'online' | 'leads' | 'products' | 'map' | 'insights' | 'socialPosts';

/** Which part of the product a widget reports on — the picker groups by it. */
export type WidgetCategory = 'visualizer' | 'market' | 'marketing';

export const WIDGET_CATEGORIES: WidgetCategory[] = ['visualizer', 'market', 'marketing'];

/** Columns in the grid. Twelve divides by 2, 3, 4 and 6, so halves and thirds both land. */
export const GRID_COLS = 12;

/** One row unit in pixels. Small enough that height feels continuous when dragged. */
export const ROW_HEIGHT = 28;

export const GRID_MARGIN: [number, number] = [16, 16];

/**
 * A widget's place on the grid: column, row, width, height — all in grid
 * units. Free placement, so a third-width widget can sit on the right with
 * nothing to its left, and two widgets can stack in one column.
 */
export interface WidgetPlacement {
    id: WidgetId;
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * Empty on purpose.
 *
 * A dashboard prefilled with someone else's guess is a dashboard people scroll
 * past — and worse, it teaches that the arrangement is not theirs to change.
 * Starting from empty makes the first act on this screen a choice about what
 * they want to see.
 */
export const DEFAULT_LAYOUT: WidgetPlacement[] = [];

const KEY = 'ardealy.bo.dashboard.widgets';

const read = (): Record<string, unknown[]> => {
    try {
        return JSON.parse(localStorage.getItem(KEY) || '{}');
    } catch {
        return {};
    }
};

const isId = (v: unknown): v is WidgetId => typeof v === 'string';
const num = (v: unknown, fallback: number): number =>
    typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : fallback;

/**
 * Reads a saved arrangement, including ones written before widgets could be
 * placed freely.
 *
 * The old shape was `{ id, span }` — width only, flowing left to right. Those
 * are laid back out in the order they were saved rather than dropped, because
 * a vendor who arranged their dashboard once should not be punished for our
 * changing the format underneath them.
 */
export function loadLayout(userId?: string): WidgetPlacement[] {
    if (!userId) return DEFAULT_LAYOUT;
    const saved = read()[userId];
    // An empty array is a vendor who removed everything, not a missing layout.
    if (!Array.isArray(saved)) return DEFAULT_LAYOUT;

    let cursorX = 0;
    let cursorY = 0;

    return saved
        .map((raw): WidgetPlacement | null => {
            const p = raw as Record<string, unknown>;
            if (!p || !isId(p.id)) return null;

            if (typeof p.x === 'number' && typeof p.w === 'number') {
                return { id: p.id, x: num(p.x, 0), y: num(p.y, 0), w: num(p.w, 4), h: num(p.h, 8) };
            }

            // Migrated from the span-only format: flow across the twelve
            // columns, wrapping, exactly as the old canvas rendered it.
            const w = num(p.span, 4);
            if (cursorX + w > GRID_COLS) {
                cursorX = 0;
                cursorY += 8;
            }
            const placed = { id: p.id, x: cursorX, y: cursorY, w, h: 8 };
            cursorX += w;
            return placed;
        })
        .filter((p): p is WidgetPlacement => p !== null);
}

export function saveLayout(userId: string | undefined, layout: WidgetPlacement[]): void {
    if (!userId) return;
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...read(), [userId]: layout }));
    } catch {
        /* a full or blocked store just means the arrangement resets next time */
    }
}

const overlaps = (a: WidgetPlacement, b: WidgetPlacement): boolean =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/** The lowest row any widget reaches. Zero for an empty dashboard. */
export const contentBottom = (layout: WidgetPlacement[]): number =>
    layout.reduce((max, p) => Math.max(max, p.y + p.h), 0);

/**
 * Rows only go as deep as the widgets themselves.
 *
 * Free horizontal placement is wanted — a third-width widget on the right with
 * nothing beside it is a real choice. Free VERTICAL placement is not: dragging
 * the only widget on the dashboard to row nine leaves a screen of nothing above
 * it, which nobody means to do.
 *
 * So a widget may sit anywhere within the space its neighbours already occupy,
 * but may not push the dashboard downward by moving. Only being tall does that:
 * the allowance is `max(what others reach, this widget's own height)`, which is
 * why resizing still grows the grid while dragging cannot.
 *
 * Pulling a widget up has to watch where it lands. The grid refuses collisions
 * during the drag, so a dropped position is always clear — but the position it
 * is pulled back to might not be, and a correction that creates an overlap is
 * worse than the thing it corrects. It takes the lowest clear row it can reach,
 * and failing that, the first place it fits at all.
 *
 * Applied on drop rather than during the drag — `onLayoutChange` fires on drag
 * stop, so feeding a corrected layout back mid-gesture is not a risk.
 */
export function clampRows(layout: WidgetPlacement[]): WidgetPlacement[] {
    const settled: WidgetPlacement[] = [];

    layout.forEach((p, index) => {
        const others = [...settled, ...layout.slice(index + 1)].filter((q) => q.id !== p.id);
        const x = Math.max(0, Math.min(p.x, GRID_COLS - p.w));
        const othersReach = others.reduce((max, q) => Math.max(max, q.y + q.h), 0);
        const maxY = Math.max(othersReach, p.h) - p.h;

        if (p.y >= 0 && p.y <= maxY) {
            settled.push({ ...p, x });
            return;
        }

        // As close to where it was left as the rule allows, and clear.
        for (let y = Math.max(0, maxY); y >= 0; y -= 1) {
            const candidate = { ...p, x, y };
            if (!others.some((q) => overlaps(candidate, q))) {
                settled.push(candidate);
                return;
            }
        }

        settled.push({ ...p, ...nextPlacement(others, p.w, p.h) });
    });

    return settled;
}

/**
 * Where a newly added widget goes: the first place it fits, reading the grid
 * the way a page is read — left to right, top to bottom.
 *
 * Beside an existing widget when there is room, rather than always on a new row
 * below. A dashboard holding one half-width card has an obvious empty half, and
 * putting the next widget underneath it instead is a layout nobody would have
 * chosen by hand.
 */
export function nextPlacement(
    layout: WidgetPlacement[],
    w: number,
    h: number,
): { x: number; y: number; w: number; h: number } {
    const bottom = contentBottom(layout);

    for (let y = 0; y <= bottom; y += 1) {
        for (let x = 0; x + w <= GRID_COLS; x += 1) {
            const candidate = { id: 'probe' as WidgetId, x, y, w, h };
            if (!layout.some((p) => overlaps(candidate, p))) return { x, y, w, h };
        }
    }

    // Nothing fits alongside; it starts a row of its own.
    return { x: 0, y: bottom, w, h };
}

export interface Box {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * How much two boxes share, as a fraction of the smaller one.
 *
 * Measured against the smaller deliberately: a small widget dragged onto a
 * large one can never cover much of the large one, so scoring against either
 * the larger or the union would make small-onto-large swaps nearly impossible
 * to trigger while large-onto-small fired instantly.
 */
export function overlapRatio(a: Box, b: Box): number {
    const w = Math.min(a.left + a.width, b.left + b.width) - Math.max(a.left, b.left);
    const h = Math.min(a.top + a.height, b.top + b.height) - Math.max(a.top, b.top);
    if (w <= 0 || h <= 0) return 0;

    const smaller = Math.min(a.width * a.height, b.width * b.height);
    return smaller > 0 ? (w * h) / smaller : 0;
}

/**
 * How far a widget must cover another before they are offered as a swap.
 *
 * Half. Touching an edge must do nothing — that is the whole complaint about
 * grids that rearrange themselves — and a vendor who has dragged a widget
 * halfway on top of another has plainly aimed at it.
 */
export const SWAP_THRESHOLD = 0.5;

/**
 * Two widgets trade places, each keeping its own size.
 *
 * Only x and y move. Exchanging the whole slot was the obvious way to
 * guarantee a valid layout, but it is not what trading places means to
 * anyone looking at it — a card that was small arriving somewhere large and
 * stretching to fill it reads as the dashboard having done something else
 * entirely. A widget is a size; where it sits is separate.
 *
 * The cost is that the result is not always legal, since a wide widget can
 * land in a narrow gap. `canSwap` is the guard, and nothing offers a swap it
 * cannot make.
 */
export function swapPositions(layout: WidgetPlacement[], aId: string, bId: string): WidgetPlacement[] {
    const a = layout.find((p) => p.id === aId);
    const b = layout.find((p) => p.id === bId);
    if (!a || !b || a === b) return layout;

    return layout.map((p) => {
        if (p.id === aId) return { ...p, x: b.x, y: b.y };
        if (p.id === bId) return { ...p, x: a.x, y: a.y };
        return p;
    });
}

/**
 * Two widgets trade places: they swap ORDER, and close up behind themselves.
 *
 * Trading raw coordinates was the obvious reading and the wrong one. Widgets
 * are different widths, so giving each the other's origin overlaps whenever
 * they are not the same size — the wide one, arriving at the narrow one's
 * corner, ran over its neighbour and got bumped sideways. What it looks like
 * from outside is one card shoving another along, which is not a swap.
 *
 * The pair is treated as a run instead. Whichever came second now starts where
 * the first did, and the first follows immediately after it. The run begins in
 * the same place and can only get shorter, so the trade cannot spill into
 * anything beside it, and each widget keeps its own size.
 *
 * The axis is whichever the two are further apart on: side by side swap left
 * for right, stacked swap top for bottom.
 */
export function swapAndSettle(layout: WidgetPlacement[], aId: string, bId: string): WidgetPlacement[] {
    const a = layout.find((p) => p.id === aId);
    const b = layout.find((p) => p.id === bId);
    if (!a || !b || a === b) return layout;

    const horizontal =
        Math.abs(a.x + a.w / 2 - (b.x + b.w / 2)) >= Math.abs(a.y + a.h / 2 - (b.y + b.h / 2));

    const moved = new Map<string, WidgetPlacement>();

    if (horizontal) {
        const [first, second] = a.x <= b.x ? [a, b] : [b, a];
        // Shifted left only if the pair would now run off the edge.
        const startX = Math.max(0, Math.min(first.x, GRID_COLS - (first.w + second.w)));
        moved.set(second.id, { ...second, x: startX });
        moved.set(first.id, { ...first, x: startX + second.w });
    } else {
        const [first, second] = a.y <= b.y ? [a, b] : [b, a];
        const startY = Math.max(0, first.y);
        moved.set(second.id, { ...second, y: startY });
        moved.set(first.id, { ...first, y: startY + second.h });
    }

    const swapped = layout.map((p) => moved.get(p.id) ?? p);

    /*
     * A safety net, not the mechanism. Closing the run up can only free space
     * between the two, so the pair cannot land on a third widget — unless one
     * was already sitting in a gap between them, which is the one case this
     * catches. The two that were traded keep what they were given.
     */
    const placed: WidgetPlacement[] = [];
    [...moved.keys(), ...swapped.filter((p) => !moved.has(p.id)).map((p) => p.id)].forEach((id) => {
        const p = swapped.find((q) => q.id === id)!;
        const fits = p.x + p.w <= GRID_COLS && !placed.some((q) => overlaps(p, q));
        placed.push(fits ? p : { ...p, ...nextPlacement(placed, p.w, p.h) });
    });

    return clampRows(layout.map((p) => placed.find((q) => q.id === p.id)!));
}

/**
 * Which side of the target the dragged widget is coming from.
 *
 * The highlight goes on that one edge rather than around the whole card. A
 * full outline says "this is selected"; an edge says "this is the one you are
 * about to take the place of", and points back at where you came from.
 */
export function swapEdge(dragged: Box, target: Box): 'left' | 'right' | 'top' | 'bottom' {
    const dx = dragged.left + dragged.width / 2 - (target.left + target.width / 2);
    const dy = dragged.top + dragged.height / 2 - (target.top + target.height / 2);

    if (Math.abs(dx) >= Math.abs(dy)) return dx < 0 ? 'left' : 'right';
    return dy < 0 ? 'top' : 'bottom';
}
